import styled from "styled-components"

import { Badge, BadgeProps, spacing } from "@minuthq/meatball-ui-react"
import { TextField } from "@minuthq/meatball-ui-react"
import { Text } from "@minuthq/meatball-ui-react"

import { ParadiseProductImageThumbnail } from "src/components/Paradise/ParadiseProductImageThumbnail"
import { ParadiseTable } from "src/components/Paradise/ParadiseTable"
import { TSubscriptionCurrencyCode } from "src/data/billing/types/billingTypes"
import { TShippableProduct } from "src/data/ecommerce/ecommerceTypes"
import { prettifyApiFieldValue } from "src/data/orders/orderUtils"
import { useTableColumns } from "src/ui/GridTable/useTableColumns/useTableColumns"
import { formatPrice } from "src/utils/formatPrice"

type TAvailableShippableProduct = {
  imageUrl?: string
  productId: string
  name: string
  stockStatus?: TShippableProduct["stock_status"]
}

type TSelectedShippableProduct = {
  productId: string
  quantity: number
}

export function ParadiseShippableProductsQuantityTable({
  selectedShippableProducts,
  availableShippableProducts,
  updateShippableProducts,
  currency,
  hideTotal = false,
  disableQuantity = false,
}: {
  availableShippableProducts: TAvailableShippableProduct[]
  selectedShippableProducts: TSelectedShippableProduct[]
  updateShippableProducts: (
    shippableProducts: TSelectedShippableProduct[]
  ) => void
  currency: TSubscriptionCurrencyCode
  hideTotal?: boolean
  disableQuantity?: boolean
}) {
  const { rows, templateColumns, headerElements } =
    useTableColumns<TAvailableShippableProduct>({
      columns: [
        {
          value: "product",
          label: "Product",
          renderCell: (data) => {
            return (
              <ProductWrapper>
                <ParadiseProductImageThumbnail imageUrl={data.imageUrl ?? ""} />
                <div>
                  <Text>{data.name}</Text>
                  {data.stockStatus && data.stockStatus !== "in_stock" && (
                    <Badge
                      color={getStockBadgeColor(data.stockStatus)}
                      size="x-small"
                    >
                      {prettifyApiFieldValue(data.stockStatus)}
                    </Badge>
                  )}
                </div>
              </ProductWrapper>
            )
          },
          columnWidth: "1fr",
        },
        {
          value: "quantity",
          label: "Quantity",
          renderCell: (data) => {
            const selectedShippableProduct = selectedShippableProducts.find(
              (product) => product.productId === data.productId
            )

            const isSelected = !!selectedShippableProduct

            return (
              <div>
                <QuantityWrapper>
                  <TextField
                    type="number"
                    value={(selectedShippableProduct?.quantity ?? 0).toString()}
                    max={100}
                    min={0}
                    onChange={(value) => {
                      const parsedValue = parseInt(value)

                      if (parsedValue > 100 || parsedValue < 0) {
                        return
                      }

                      if (isSelected) {
                        updateShippableProducts(
                          selectedShippableProducts.map((product) => {
                            if (product.productId === data.productId) {
                              return {
                                ...product,
                                quantity: parseInt(value),
                              }
                            }

                            return product
                          })
                        )

                        return
                      }

                      updateShippableProducts([
                        ...selectedShippableProducts,
                        {
                          productId: data.productId,
                          quantity: parseInt(value),
                        },
                      ])
                    }}
                    size="small"
                    disabled={
                      disableQuantity || data.stockStatus === "out_of_stock"
                    }
                  />
                </QuantityWrapper>
              </div>
            )
          },
          columnWidth: "1fr",
        },
        {
          value: "total",
          label: "Total",
          renderCell: () => <div>{formatPrice(0, currency)}</div>,
          columnWidth: "auto",
          hidden: hideTotal,
        },
      ],
      data: availableShippableProducts,
    })

  return (
    <div>
      <ParadiseTable
        header={headerElements}
        rows={rows ?? []}
        templateColumns={templateColumns}
        disableRowHover
        removeMargin
      />
    </div>
  )
}

function getStockBadgeColor(
  stockStatus: TShippableProduct["stock_status"]
): BadgeProps["color"] {
  if (stockStatus === "out_of_stock") {
    return "error"
  }

  if (stockStatus === "low_stock") {
    return "warning"
  }

  return "info"
}

const ProductWrapper = styled.div`
  display: flex;
  gap: ${spacing.S};
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
`

const QuantityWrapper = styled.div`
  display: grid;
  align-items: flex-start;
  justify-content: flex-start;
`
